// ============================================================
// 阴盘奇门遁甲 排盘引擎（道家转盘时家体系）
//
// 排盘规则：
//  1. 定局（juMethod 可选）：
//     · chaibu 拆补法（默认）：冬至后阳遁、夏至后阴遁；以节气定三元局数，
//       以日柱符头（甲/己日）定上中下元，不置闰。
//     · zhishu 支数法：年支数+农历月数+农历日数+时支数之和 mod 9（余0作9），
//       阴阳遁仍按冬至/夏至 —— 即王凤麟式阴盘 APK 所用定局法。
//  2. 地盘：三奇六仪「戊己庚辛壬癸丁丙乙」，阳遁顺布、阴遁逆布，
//     自局数宫起沿九宫数字排布（此为全盘唯一走数字之处）。
//  3. 旬首取【时柱】所属六甲旬（时家奇门），旬首遁仪所在地盘宫为本位：
//     本位九星即值符、本位八门即值使；旬首仪落中五宫则天禽/死门寄坤二。
//  4. 天盘：九星连同各自所携地盘干，沿八宫环序整体旋转（转盘），
//     使值符星落时干之宫；天禽寄天芮同行。时干为甲时加旬首仪宫；
//     时干落中五宫寄坤二。
//  5. 人盘：值使门自本宫起按时辰飞宫（阳遁顺飞、阴遁逆飞九宫数字，
//     经中五照数走，落中五寄坤二）；得值使落宫后八门沿环序整体旋转。
//  6. 神盘（shenMode 可选）：
//     · yin 阴盘八神（默认）：不分阴阳遁，自值符宫一律顺时针布。
//     · classic 传统阳盘：阳遁顺时针、阴遁逆时针。
//     · nine 九神盘：阳顺阴逆 + 太常居中五 —— 与市售阴盘 APK 一致。
// ============================================================

import { Solar } from 'lunar-javascript';
import {
  GAN, ZHI, WX, GONGS, GONG_BY_NUM, STARS, STAR_BY_BEN,
  MENS, MEN_BY_BEN, MEN_RING,
  GODS, SANQI_LIUYI, LIUJIA_YI, JIXING, RUMU, RUMU_BY_WX,
  JIEQI_JU, SHUN, NI, RING_CW, YIMA, ZHI_GONG,
} from './constants.js';

const gIdx = (g) => GAN.indexOf(g);
const zIdx = (z) => ZHI.indexOf(z);
const parseGZ = (gz) => ({ gan: gz[0], zhi: gz[1] });

// 干支 → 所属六甲旬（旬首）、遁仪、旬空两支
export function xunOf(gz) {
  const { gan, zhi } = parseGZ(gz);
  const headZhi = ZHI[(zIdx(zhi) - gIdx(gan) + 12) % 12];
  const h = zIdx(headZhi);
  return {
    head: '甲' + headZhi,
    yi: LIUJIA_YI['甲' + headZhi],
    kong: [ZHI[(h + 10) % 12], ZHI[(h + 11) % 12]],
  };
}

// 数字节序：自 start 起，阳遁顺、阴遁逆（地盘布仪 / 值使飞宫专用）
function orderFrom(start, yinYang) {
  const base = yinYang === '阳' ? SHUN : NI;
  const i = base.indexOf(start);
  return base.slice(i).concat(base.slice(0, i));
}

// 环旋转：把按环位存放的 8 元素数组整体旋转，使 fromPos 位元素落到 toPos 位
function rotateRing(items, fromPos, toPos) {
  const n = items.length;
  const shift = ((toPos - fromPos) % n + n) % n;
  const out = new Array(n);
  for (let i = 0; i < n; i++) out[(i + shift) % n] = items[i];
  return out;
}

// 地盘：阳遁自局宫顺布六仪、逆布三奇；阴遁反之
function buildDiPan(ju, yinYang) {
  const dp = {};
  const seq = orderFrom(ju, yinYang);
  SANQI_LIUYI.forEach((gan, i) => { dp[seq[i]] = gan; });
  return dp;
}

// 天盘九星环：每环位放「坐守该宫之星的本宫号」；天禽(5)寄坤二(2)随芮同行
function buildStarRing() {
  const ring = Array.from({ length: 8 }, () => []);
  STARS.forEach((s) => {
    if (s.ben !== 5) ring[RING_CW.indexOf(s.ben)].push(s.ben);
  });
  ring[RING_CW.indexOf(2)].push(5);
  return ring;
}

// 五行相克
function ke(a, b) {
  return (a === '木' && b === '土') || (a === '土' && b === '水') || (a === '水' && b === '火') ||
         (a === '火' && b === '金') || (a === '金' && b === '木');
}

// 四害判定（空亡 / 门迫 / 击刑 / 入墓）
function computeHarms({ g, dipan, tianpanGans, menName, starNames, riKong, shiKong }) {
  const harms = [];
  // 空亡：宫内地支逢日空 / 时空
  g.zhis.forEach((z) => {
    if (riKong.includes(z)) harms.push({ type: '空亡', label: '日空', detail: `宫内地支${z}逢日空亡（${riKong.join('')}）` });
    if (shiKong.includes(z)) harms.push({ type: '空亡', label: '时空', detail: `宫内地支${z}逢时空亡（${shiKong.join('')}）` });
  });
  // 门迫：门克宫为迫
  if (menName) {
    const m = MENS.find((x) => x.name === menName);
    if (m && ke(m.wx, g.wx)) harms.push({ type: '门迫', label: '迫', detail: `${menName}（${m.wx}）克${g.gua}宫（${g.wx}）` });
  }
  // 六仪击刑：天盘/地盘六仪落刑宫
  [...tianpanGans, dipan].forEach((gan, i) => {
    if (gan && JIXING[gan] === g.num) {
      harms.push({ type: '击刑', label: '刑', detail: `${i < tianpanGans.length ? '天盘' : '地盘'}${gan}落${g.gua}宫击刑` });
    }
  });
  // 入墓：天盘干入墓库
  tianpanGans.forEach((gan) => {
    if (gan && RUMU[gan] === g.num) harms.push({ type: '入墓', label: '墓', detail: `天盘${gan}入${g.gua}宫墓` });
  });
  if (dipan && RUMU[dipan] === g.num) harms.push({ type: '入墓', label: '墓', detail: `地盘${dipan}入${g.gua}宫墓` });
  // 门/星入墓（按五行库）
  if (menName) {
    const m = MENS.find((x) => x.name === menName);
    if (m && RUMU_BY_WX[m.wx] === g.num) harms.push({ type: '入墓', label: '墓', detail: `${menName}入${g.gua}宫墓` });
  }
  starNames.forEach((sn) => {
    const s = STARS.find((x) => x.name === sn);
    if (s && RUMU_BY_WX[s.wx] === g.num && s.ben !== 5) harms.push({ type: '入墓', label: '墓', detail: `${sn}入${g.gua}宫墓` });
  });
  return harms;
}

export function computeChart({ year, month, day, hour, minute = 0, shenMode = 'yin', juMethod = 'chaibu' }) {
  if (!year || !month || !day || hour == null) throw new Error('请提供完整的年月日时');
  const solar = Solar.fromYmdHms(year, month, day, hour, minute, 0);
  const lunar = solar.getLunar();

  const yGZ = lunar.getYearInGanZhiExact();   // 年柱以立春为界（奇门通例）
  const mGZ = lunar.getMonthInGanZhi();
  // 晚子时（23:00 起）日柱进位 —— 奇门通行制式
  const dGZ = lunar.getDayInGanZhiExact();
  const hGZ = lunar.getTimeInGanZhi();
  const dayGZ = parseGZ(dGZ);
  const hourGZ = parseGZ(hGZ);

  const dayXun = xunOf(dGZ);
  const hourXun = xunOf(hGZ);
  const riKong = dayXun.kong;
  const shiKong = hourXun.kong;

  // ---- 定节气与阴阳遁（getPrevJieQi 含精确交节时刻）----
  const jq = lunar.getPrevJieQi();
  const jqName = jq.getName();
  const conf = JIEQI_JU[jqName];
  if (!conf) throw new Error('未知节气: ' + jqName);
  const yinYang = conf[0];
  const jqSolar = jq.getSolar();
  const jieqiDate = { y: jqSolar.getYear(), m: jqSolar.getMonth(), d: jqSolar.getDay(), time: jqSolar.toYmdHms() };

  // ---- 定局 ----
  let ju, yuan = '', fuTouGZ = '';
  if (juMethod === 'zhishu') {
    // 王凤麟式阴盘支数定局：年支数+农历月数+农历日数+时支数，和 mod 9（余0作9）
    // 支数取地支序数（子1丑2…亥12）；晚子时（23点起）随日柱进位取次日农历月日；闰月按本月计
    const zhiNum = (gz) => zIdx(parseGZ(gz).zhi) + 1;
    const dRoll = new Date(year, month - 1, day + (hour >= 23 ? 1 : 0));
    const lEff = Solar.fromYmd(dRoll.getFullYear(), dRoll.getMonth() + 1, dRoll.getDate()).getLunar();
    const lunarMonth = Math.abs(lEff.getMonth());
    const lunarDay = lEff.getDay();
    const sum = zhiNum(yGZ) + lunarMonth + lunarDay + zhiNum(hGZ);
    ju = sum % 9 === 0 ? 9 : sum % 9;
    yuan = '支数';
    fuTouGZ = '—';
  } else {
    // 拆补法定三元：只看日柱符头（甲/己日，子午卯酉上元、寅申巳亥中元、辰戌丑未下元）
    const dz = zIdx(dayGZ.zhi);
    const gi = gIdx(dayGZ.gan);
    const fhGan = gi < 5 ? '甲' : '己';                 // 甲~戊归甲头；己~癸归己头（己日本身即符头）
    const back = gi === 5 ? 0 : (gi < 5 ? gi : gi - 5); // 距符头天数
    const fhZhi = ZHI[(dz - back + 12) % 12];
    const yuanGroup = '子午卯酉'.includes(fhZhi) ? 1 : ('寅申巳亥'.includes(fhZhi) ? 2 : 3);
    fuTouGZ = fhGan + fhZhi;
    yuan = ['上', '中', '下'][yuanGroup - 1];
    ju = conf[yuanGroup];
  }

  // ---- 地盘 ----
  const dp = buildDiPan(ju, yinYang);

  // ---- 旬首（时柱旬）→ 值符星 / 值使门 ----
  const yi = hourXun.yi;
  const xunShou = hourXun.head;
  let p0 = Number(Object.keys(dp).find((k) => dp[k] === yi));   // 旬首仪所落地盘宫
  const p0Raw = p0;
  if (p0 === 5) p0 = 2;                                          // 中五寄坤二
  const zhiFuStar = STAR_BY_BEN[p0Raw];                          // 旬首仪在中宫 → 天禽作值符
  const zhiFu = zhiFuStar.name;
  const zhiShiMen = MEN_BY_BEN[p0Raw === 5 ? 2 : p0Raw];         // 中宫无门 → 寄坤二死门
  const zhiShi = zhiShiMen.name;

  // ---- 天盘：九星携干整体转盘 ----
  let pt;
  if (hourGZ.gan === '甲') pt = p0;                              // 时干为甲，加旬首仪宫
  else pt = Number(Object.keys(dp).find((k) => dp[k] === hourGZ.gan));
  const ptRaw = pt;
  if (pt === 5) pt = 2;                                          // 时干落中宫，寄坤二

  const starRing = rotateRing(buildStarRing(), RING_CW.indexOf(p0Raw === 5 ? 2 : p0Raw), RING_CW.indexOf(pt));

  // ---- 人盘：值使飞宫 + 八门转盘 ----
  const flyOrigin = p0Raw === 5 ? 2 : p0Raw;
  const step = (zIdx(hourGZ.zhi) - zIdx(xunShou[1]) + 12) % 12;
  const flySeq = orderFrom(flyOrigin, yinYang);
  let zsGong = flySeq[step % 9];
  if (zsGong === 5) zsGong = 2;                                  // 落中五寄坤二

  const menRing = rotateRing(
    MEN_RING.slice(),
    RING_CW.indexOf(zhiShiMen.ben),
    RING_CW.indexOf(zsGong),
  );

  // ---- 神盘：自值符宫起布 ----
  // yin：阴盘八神不分阴阳遁一律顺时针；classic：传统阳盘阳顺阴逆（八神）；
  // nine：本 APK 法度 —— 阳顺阴逆 + 九神盘（太常居中五）
  const godsRing = new Array(8);
  const start = RING_CW.indexOf(pt);
  GODS.forEach((name, k) => {
    const pos = shenMode === 'yin'
      ? (start + k) % 8
      : ((start + (yinYang === '阳' ? k : -k)) % 8 + 8) % 8;
    godsRing[pos] = name;
  });
  const centerShen = shenMode === 'nine' ? '太常' : null;

  // ---- 组装九宫 ----
  const starsAt = {}, gansAt = {}, mensAt = {}, godsAt = {};
  for (let i = 0; i < 8; i++) {
    const palace = RING_CW[i];
    starsAt[palace] = starRing[i].map((h) => STAR_BY_BEN[h].name);
    gansAt[palace] = starRing[i].map((h) => dp[h]);              // 星携本宫地盘干
    mensAt[palace] = menRing[i];
    godsAt[palace] = godsRing[i];
  }
  // 九神盘：太常居中五 —— 同样写进神盘映射，避免 meta 与 palaces 两处不一致
  if (centerShen) godsAt[5] = centerShen;

  // 驿马（时支马为主，附日支马）
  const maZhi = YIMA[hourGZ.zhi];
  const maGong = ZHI_GONG[maZhi];
  const dayMaZhi = YIMA[dayGZ.zhi];

  const palaces = GONGS.map((g) => {
    if (g.num === 5) {
      return {
        num: 5, gua: '中', wx: '土', dir: '中', zhis: [],
        dipan: dp[5], tianpan: [], stars: [], men: null,
        shen: centerShen,
        harms: [], center: true, isMa: false,
        note: centerShen ? '中五宫无星门，太常居中（九神盘）' : '中五宫无星门神，天禽寄坤二同行',
      };
    }
    const tianpanGans = gansAt[g.num];
    const starNames = starsAt[g.num];
    const menName = mensAt[g.num];
    const shenName = godsAt[g.num];
    const harms = computeHarms({
      g, dipan: dp[g.num], tianpanGans, menName, starNames, riKong, shiKong,
    });
    return {
      num: g.num, gua: g.gua, wx: g.wx, dir: g.dir, zhis: g.zhis,
      dipan: dp[g.num],
      jigongDiGan: starNames.includes('天禽') ? dp[5] : null, // 天禽寄坤：地盘行加显中五地盘干
      tianpan: tianpanGans,
      stars: starNames,
      men: menName,
      shen: shenName,
      harms,
      center: false,
      isMa: g.num === maGong,
      isZhiFu: starNames.includes(zhiFu),
      isZhiShi: menName === zhiShi,
    };
  });

  return {
    input: { year, month, day, hour, minute, shenMode, juMethod },
    solarText: solar.toYmdHms(),
    fourPillars: { year: yGZ, month: mGZ, day: dGZ, hour: hGZ },
    yinYang, ju, yuan, jieqi: jqName, jieqiDate,
    fuTou: fuTouGZ,
    xunShou, xunShouYi: yi, xunFromHour: true,
    zhiFu, zhiShi,
    zhiFuCenter: p0Raw === 5,
    riKong, shiKong,
    maXing: { zhi: maZhi, gong: maGong },
    dayMa: dayMaZhi,
    palaces,
    // 供校验/调试的全盘映射
    meta: {
      diPan: dp, tianPan: gansAt, starMap: starsAt, menMap: mensAt, godMap: godsAt,
      fuTou: fuTouGZ, step, zsGong, p0: p0Raw, pt: ptRaw,
    },
  };
}
